import { useRef, useState } from 'react'
import { Button, Group, Paper, Textarea } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { normalizeErrorString } from '@medplum/core'
import { useMedplum, useMedplumProfile } from '@medplum/react'
import { IconCircleCheck, IconCircleOff, IconPaperclip, IconSend } from '@tabler/icons-react'
import { VoiceInputButton, type VoiceInputButtonHandle } from '@/components/ui'
import {
  createThreadFileMessage,
  createThreadMessage,
  type MessageThreadSummary,
  type PractitionerThreadOption,
  type ThreadMessage,
} from '../service'
import { PRIMARY_BUTTON_PROPS } from '../tokens'

type ThreadMessageComposerProps = {
  threadSummary: MessageThreadSummary
  disabled?: boolean
  onCreated?: (message: ThreadMessage) => void
  minimized?: boolean
  /** Tighter padding and icon-only actions for the redesigned Messages page. */
  compact?: boolean
  /**
   * Delegate text sends to the caller so it can own the optimistic
   * Sending/Failed bubble and its Retry action. Resolves false on failure, in
   * which case the caller is already showing the error and the composer keeps
   * quiet rather than also raising a toast. When omitted the composer creates
   * the message itself, which is what the legacy minimized view still does.
   */
  onSendText?: (text: string) => Promise<boolean>
  /**
   * Focus/blur on the message box, which the thread treats as "the user is
   * looking at this conversation" for read receipts.
   */
  onPresenceChange?: (present: boolean) => void
  /** Typing in the box — the same presence signal, for a already-focused box. */
  onActivity?: () => void
}

export const ThreadMessageComposer = ({
  threadSummary,
  disabled = false,
  onCreated,
  minimized = false,
  compact = false,
  onSendText,
  onPresenceChange,
  onActivity,
}: ThreadMessageComposerProps) => {
  const medplum = useMedplum()
  const profile = useMedplumProfile()
  const practitionerProfile = profile?.resourceType === 'Practitioner' ? profile : undefined

  const [value, setValue] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [micError, setMicError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const voiceInputRef = useRef<VoiceInputButtonHandle>(null)
  const valueRef = useRef('')
  const stoppingVoiceRef = useRef(false)

  const updateValue = (next: string) => {
    valueRef.current = next
    setValue(next)
  }

  // Shared by both composer layouts so presence behaves identically in the
  // redesigned page and the minimized patient panel.
  const presenceProps = {
    onFocus: () => onPresenceChange?.(true),
    onBlur: () => onPresenceChange?.(false),
  }

  const handleTextChange = (next: string) => {
    updateValue(next)
    onActivity?.()
  }

  const recipients = [threadSummary.sender, ...threadSummary.recipients]
    .filter((participant): participant is PractitionerThreadOption =>
      Boolean(
        participant &&
        participant.practitioner.id &&
        participant.practitioner.id !== practitionerProfile?.id,
      ),
    )
    .map((participant) => participant.practitioner)

  const handleSubmit = async (): Promise<void> => {
    if (!practitionerProfile?.id) {
      showNotification({
        color: 'red',
        icon: <IconCircleOff size={16} />,
        title: 'Unable to send message',
        message: 'Current Medplum profile is not a practitioner.',
      })
      return
    }

    if (disabled || stoppingVoiceRef.current) {
      return
    }

    stoppingVoiceRef.current = true
    try {
      await voiceInputRef.current?.stop()
      setMicError(null)

      const message = valueRef.current.trim()
      if (!message) {
        return
      }

      // Delegated path: clear the box immediately so the optimistic bubble is
      // the single place the send's progress is reported.
      if (onSendText) {
        updateValue('')
        const sent = await onSendText(message)
        if (!sent) {
          // Keep the text in the failed bubble, not back in the composer —
          // Retry there re-sends the exact message the user wrote.
          setMicError(null)
        }
        return
      }

      setSubmitting(true)
      try {
        const createdMessage = await createThreadMessage({
          thread: threadSummary.thread,
          sender: practitionerProfile,
          recipients,
          message,
          medplum,
        })

        updateValue('')
        onCreated?.({
          message: createdMessage,
          sender: practitionerProfile,
          recipients,
        })
      } catch (err) {
        showNotification({
          color: 'red',
          icon: <IconCircleOff size={16} />,
          title: 'Unable to send message',
          message: normalizeErrorString(err),
        })
      } finally {
        setSubmitting(false)
      }
    } finally {
      stoppingVoiceRef.current = false
    }
  }

  const handleFileUpload = async (file: File | null): Promise<void> => {
    if (!file) {
      return
    }

    if (!practitionerProfile?.id) {
      showNotification({
        color: 'red',
        icon: <IconCircleOff size={16} />,
        title: 'Unable to upload file',
        message: 'Current Medplum profile is not a practitioner.',
      })
      return
    }

    if (disabled) {
      return
    }

    setSubmitting(true)
    try {
      const createdMessage = await createThreadFileMessage({
        thread: threadSummary.thread,
        sender: practitionerProfile,
        recipients,
        file,
        medplum,
      })

      onCreated?.({
        message: createdMessage,
        sender: practitionerProfile,
        recipients,
      })

      showNotification({
        color: 'green',
        icon: <IconCircleCheck size={16} />,
        message: 'File uploaded',
      })
    } catch (err) {
      showNotification({
        color: 'red',
        icon: <IconCircleOff size={16} />,
        title: 'Unable to upload file',
        message: normalizeErrorString(err),
      })
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      setSubmitting(false)
    }
  }

  return (
    <Paper className={compact ? 'bg-transparent p-3' : 'p-4'}>
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={(event) => void handleFileUpload(event.currentTarget.files?.[0] ?? null)}
      />
      {compact ? (
        <Group align="flex-end" gap="xs" wrap="nowrap">
          <Textarea
            value={value}
            onChange={(event) => handleTextChange(event.currentTarget.value)}
            {...presenceProps}
            placeholder={disabled ? 'Archived threads are read-only' : 'Write a message'}
            aria-label="Write a message"
            minRows={1}
            autosize
            maxRows={5}
            disabled={disabled}
            radius="md"
            className="flex-1"
            classNames={{ input: 'border-[#E4E7EB] !text-sm text-[#1F2733]' }}
          />
          <Button
            variant="default"
            radius="md"
            styles={{ root: { borderColor: '#E4E7EB' } }}
            onClick={() => fileInputRef.current?.click()}
            loading={submitting}
            disabled={disabled || recipients.length === 0}
            aria-label="Attach file"
          >
            <IconPaperclip size={18} className="text-[#6B7280]" />
          </Button>
          <VoiceInputButton
            ref={voiceInputRef}
            value={value}
            onChange={updateValue}
            disabled={disabled || submitting}
            onErrorChange={setMicError}
            size="md"
          />
          <Button
            {...PRIMARY_BUTTON_PROPS}
            onClick={() => void handleSubmit()}
            loading={submitting}
            disabled={disabled || !value.trim() || recipients.length === 0}
            aria-label="Send message"
          >
            <IconSend size={18} />
          </Button>
        </Group>
      ) : (
        <>
          <Textarea
            value={value}
            onChange={(event) => handleTextChange(event.currentTarget.value)}
            {...presenceProps}
            placeholder={disabled ? 'Archived threads are read-only' : 'Write a new message'}
            minRows={minimized ? 2 : 3}
            autosize
            maxRows={minimized ? 5 : 6}
            disabled={disabled}
          />
          <Group justify="flex-start" mt="sm" gap={minimized ? 'xs' : 'sm'}>
            <Button
              variant="light"
              onClick={() => fileInputRef.current?.click()}
              loading={submitting}
              disabled={disabled || recipients.length === 0}
              leftSection={!minimized && <IconPaperclip size={16} />}
            >
              {minimized ? <IconPaperclip size={20} /> : 'Attach file'}
            </Button>
            <VoiceInputButton
              ref={voiceInputRef}
              value={value}
              onChange={updateValue}
              disabled={disabled || submitting}
              onErrorChange={setMicError}
              size={minimized ? 'md' : 'lg'}
            />
            <Button
              onClick={() => void handleSubmit()}
              loading={submitting}
              disabled={disabled || !value.trim() || recipients.length === 0}
              leftSection={!minimized && <IconSend size={16} />}
            >
              {minimized ? <IconSend size={20} /> : 'Send'}
            </Button>
          </Group>
        </>
      )}
      {micError && <p className="mt-2 text-xs text-red-600">{micError}</p>}
    </Paper>
  )
}
